import { ReactNode, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, X } from "lucide-react";
import { isValidAsin } from "@/utils/asin";

interface AddHeroAsinModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddAsins: (asins: string[]) => void | Promise<void>;
  title?: string;
  description?: ReactNode;
  secondaryAction?: { label: string; onClick: () => void };
}

export function AddHeroAsinModal({
  open,
  onOpenChange,
  onAddAsins,
  title = "Add Hero ASIN(s)",
  description,
  secondaryAction,
}: AddHeroAsinModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [parsedAsins, setParsedAsins] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parseAsins = (value: string) => {
    const asins = value
      .split(/[\n,\s]+/)
      .map((asin) => asin.trim().toUpperCase())
      .filter((asin) => asin.length > 0)
      .filter((asin) => isValidAsin(asin));

    return [...new Set(asins)];
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setParsedAsins(parseAsins(value));
  };

  const handleRemoveAsin = (asinToRemove: string) => {
    const remaining = parsedAsins.filter((asin) => asin !== asinToRemove);
    setParsedAsins(remaining);
    setInputValue(remaining.join("\n"));
  };

  const resetForm = () => {
    setInputValue("");
    setParsedAsins([]);
  };

  const handleDialogOpenChange = (next: boolean) => {
    if (!next && isSubmitting) return;
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (parsedAsins.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await Promise.resolve(onAddAsins(parsedAsins));
      resetForm();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultDescription = "Enter one or more ASINs to add as Hero products. Separate multiple ASINs with commas, spaces, or new lines.";

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild={typeof description !== "string"}>
            {description ?? defaultDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="asins">ASIN(s)</Label>
            <Textarea
              id="asins"
              placeholder={"B0ABC12345\nB0DEF67890\nB0GHI23456"}
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              disabled={isSubmitting}
              className="min-h-[120px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Valid ASINs start with "B0" followed by 8 alphanumeric characters
            </p>
          </div>

          {parsedAsins.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                {parsedAsins.length} valid ASIN{parsedAsins.length !== 1 ? "s" : ""} detected:
              </Label>
              <div className="flex flex-wrap gap-2">
                {parsedAsins.map((asin) => (
                  <Badge key={asin} variant="secondary" className="font-mono text-xs pr-1">
                    {asin}
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => handleRemoveAsin(asin)}
                      className="ml-1 hover:bg-muted rounded-full p-0.5 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {secondaryAction && (
            <Button
              variant="ghost"
              className="sm:mr-auto"
              onClick={() => {
                secondaryAction.onClick();
                handleDialogOpenChange(false);
              }}
              disabled={isSubmitting}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleDialogOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={parsedAsins.length === 0 || isSubmitting}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add {parsedAsins.length > 0 ? parsedAsins.length : ""} ASIN
            {parsedAsins.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
